import React, { useCallback, useEffect, useMemo } from 'react'
import { CellProps, Column } from 'react-table'
import { useTranslation } from 'react-i18next'
import { toast } from '@src/helpers/toaster'
import { AxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import Palm from '@components/Icons/Palm'
import { IconUserOff } from '@tabler/icons-react'
import { OrgStructureRecord } from '@pages/Team/Members/types'
import Actions from '@pages/Team/Members/List/components/Columns/Actions'
import Role from '@pages/Team/Members/List/components/Columns/Role'
import Label from '@pages/Team/Members/List/components/Columns/Label'
import SelectHeader from '@pages/Team/Members/List/components/Columns/SelectHeader'
import SelectCell from '@pages/Team/Members/List/components/Columns/SelectCell'

import { useIsAdmin } from '@src/helpers'
import { useGetTaskStatus } from '@queries/OrgStructure'
import { OrgStructureKeys } from '@queries/Keys'

import { TimeOffDuration } from '@type/common'
import Source from '@pages/Team/Members/List/components/Columns/Source'
import Status from '@pages/Team/Members/List/components/Columns/Status'

interface UseGetTableColumnsProps {
  onDeleteRecord: (record: OrgStructureRecord) => void
  onSuspendRecord: (record: OrgStructureRecord) => void
}

export const useGetTableColumns = ({ onDeleteRecord, onSuspendRecord }: UseGetTableColumnsProps) => {
  const isAdmin = useIsAdmin()
  const { t } = useTranslation('org')
  const { t: tCommon } = useTranslation('common')

  const getTimeOffString = useCallback((record: OrgStructureRecord, prop: 'limit' | 'duration') => {
    let amount = record.time_off[prop]
    if (!amount) {
      amount = 0
    }
    const type = (record.time_off.type || TimeOffDuration.day) as TimeOffDuration
    const postfix = tCommon(type, { count: amount })
    return `${amount} ${postfix} `
  }, [])

  return useMemo<Column<OrgStructureRecord>[]>(() => {
    const actionCol = isAdmin
      ? [
          {
            id: 'actions',
            disableSortBy: true,
            accessor: (record: OrgStructureRecord) => <Actions record={record} onDeleteRecord={onDeleteRecord} onSuspendRecord={onSuspendRecord} />,
          },
        ]
      : []

    const selectCol = isAdmin
      ? [
          {
            // The header is a select-all checkbox and the cell is a
            // per-row checkbox — there's nothing meaningful to sort by,
            // so suppress the sort chevron that would otherwise overlap
            // the checkbox.
            disableSortBy: true,
            Header: () => <SelectHeader />,
            id: 'select_row',
            Cell: (cell: CellProps<OrgStructureRecord>) => <SelectCell data={cell} />,
          },
        ]
      : []
    return [
      ...selectCol,
      {
        Header: t('tableRows.fullNameTitle'),
        id: 'full_name',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.full_name,
        Cell: (cell: CellProps<OrgStructureRecord>) => {
          const record = cell.row.original
          const icons = []
          if (record.is_vacation) icons.push(<Palm key="vacation" className="h-4 w-4" />)
          if (record.status === 'suspended')
            icons.push(<IconUserOff key="suspended" className="h-4 w-4 text-red-500" />)
          return icons.length > 0 ? (
            <div className="flex items-center gap-1">
              {icons}
              <span className={record.status === 'suspended' ? 'text-gray-400' : ''}>{record.full_name}</span>
            </div>
          ) : (
            record.full_name
          )
        },
      },
      {
        Header: t('tableRows.emailTitle'),
        accessor: 'email',
        defaultCanSort: true,
      },
      {
        Header: t('tableRows.roleLabel'),
        id: 'role',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.auth_user_role ?? '',
        Cell: (cell: CellProps<OrgStructureRecord>) => <Role record={cell.row.original} />,
      },
      {
        Header: t('tableRows.departmentTitle'),
        id: 'department',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.department?.name ?? '',
        Cell: (cell: CellProps<OrgStructureRecord>) => (
          <Label value={cell.row.original.department?.name} classNames="bg-purple-50 border-purple-200 text-brand_primary" />
        ),
      },
      {
        Header: t('tableRows.jobTitle'),
        id: 'job_title',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.job_role?.name ?? '',
        Cell: (cell: CellProps<OrgStructureRecord>) => (
          <Label value={cell.row.original.job_role?.name} classNames="bg-emerald-50 border-emerald-200 text-emerald-700" />
        ),
      },
      {
        Header: t('tableRows.locationTitle'),
        id: 'location',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.location?.name ?? '',
        Cell: (cell: CellProps<OrgStructureRecord>) => (
          <Label value={cell.row.original.location?.name} classNames="bg-sky-50 border-sky-200 text-sky-700" />
        ),
      },
      {
        Header: t('tableRows.managerTitle'),
        id: 'manager',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.manager?.full_name ?? '',
      },
      {
        Header: t('tableRows.statusTitle'),
        id: 'status',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.status || 'active',
        Cell: (cell: CellProps<OrgStructureRecord>) => <Status record={cell.row.original} />,
      },
      {
        Header: t('tableRows.timeOffTaken'),
        id: 'time_off_taken',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => getTimeOffString(record, 'duration'),
      },
      {
        Header: t('tableRows.timeOffLimit'),
        id: 'time_off_limit',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => getTimeOffString(record, 'limit'),
      },
      {
        Header: () => <div className="flex justify-center">{t('tableRows.source')}</div>,
        id: 'source',
        defaultCanSort: true,
        accessor: (record: OrgStructureRecord) => record.source ?? '',
        Cell: (cell: CellProps<OrgStructureRecord>) => <Source user={cell.row.original} />,
      },
      ...actionCol,
    ]
  }, [])
}

export const useCheckImportTaskStatus = (
  importTaskId: string | number | null,
  setImportTaskId: (id: string | number | null) => void,
) => {
  const { mutateAsync: fetchTaskStatus } = useGetTaskStatus()
  const cache = useQueryClient()

  const checkImportStatus = async () => {
    if (!importTaskId) {
      return
    }
    try {
      const isTaskCompleted = await fetchTaskStatus(importTaskId)

      if (isTaskCompleted) {
        setImportTaskId(null)
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.invalidateQueries([OrgStructureKeys.Filters])
      } else {
        setTimeout(() => checkImportStatus(), 1000)
      }
    } catch (e) {
      toast.error((e as AxiosError).message)
    }
  }

  useEffect(() => {
    checkImportStatus()
  }, [importTaskId])
}
